import { useState, useCallback } from 'react';

/**
 * Custom hook to manage multiple modal states in a centralized way
 * Consolidates 5+ individual modal open/close state variables into a single object
 */
const useModalStates = (initialModals = {}) => {
  const [modals, setModals] = useState({
    modeChangeSuggestion: false,
    manualAssessment: false,
    delaySettings: false,
    checkInHistory: false,
    ...initialModals
  });

  // Open a specific modal
  const openModal = useCallback((modalKey) => {
    setModals(prev => ({
      ...prev,
      [modalKey]: true
    }));
  }, []);

  // Close a specific modal
  const closeModal = useCallback((modalKey) => {
    setModals(prev => ({
      ...prev,
      [modalKey]: false
    }));
  }, []);

  // Toggle a specific modal
  const toggleModal = useCallback((modalKey) => {
    setModals(prev => ({
      ...prev,
      [modalKey]: !prev[modalKey]
    }));
  }, []);

  // Set multiple modal states at once
  const setMultipleModals = useCallback((updates) => {
    setModals(prev => ({
      ...prev,
      ...updates
    }));
  }, []);

  // Close all modals
  const closeAllModals = useCallback(() => {
    setModals(prev => 
      Object.keys(prev).reduce((acc, key) => {
        acc[key] = false;
        return acc;
      }, {})
    );
  }, []);

  // Check if a specific modal is open
  const isModalOpen = useCallback((modalKey) => modals[modalKey] || false, [modals]);

  // Check if any modal is open
  const isAnyModalOpen = Object.values(modals).some(state => state === true);

  // Check if any of the specified modals are open
  const areAnyModalsOpen = useCallback((modalKeys) => 
    modalKeys.some(key => modals[key] || false), [modals]);

  // Get count of open modals
  const openModalCount = Object.values(modals).filter(state => state === true).length;

  return {
    modals,
    openModal,
    closeModal,
    toggleModal,
    setMultipleModals,
    closeAllModals,
    isModalOpen,
    isAnyModalOpen,
    areAnyModalsOpen,
    openModalCount
  };
};

export default useModalStates;